/**
 * Always-on physical-site list for §A. Service-area chips stay on
 * ComboboxLocations (G13). Duplicate rows are removed by the operator —
 * there is no merge banner or twin chip. Coverage-without-building renders
 * in the labeled AlsoServingBand below the list (L9 #9) — never as an
 * address row.
 */
import { useRef, useState, type ReactNode } from 'react';
import { message } from 'antd';
import { confirmRemoveGathered, isConfirmedGathered } from '../confirmRemoveGathered';

import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry, BbPatchPayload, BbTable } from '@/features/brand-bible-wizard/types';

import { composeAddress, type AddressParts } from '../sections/locationMerge';
import AlsoServingBand from './AlsoServingBand';
import { coverageRowKey, hasCoverageLabel, normalizeCoverageLabel } from './alsoServing';
import { displayLocationName, nameFromStreet, slugFromLocationName } from './locationDisplay';
import { MergeLocationRow, type LocationPartField } from './MergeLocationRow';
import { LocationRowDetails, type LocationDetailsPatch } from './LocationRowDetails';
import {
  getLocationParts,
  hasTypedLocationParts,
  isAddressLocation,
  isMetroCoverageRow,
  toLocationCandidate,
} from './useLocationMerge';

/** The parts a location row needs before an edit can save (Suite optional). */
const REQUIRED_PARTS: readonly LocationPartField[] = ['street', 'city', 'state', 'zip'];

function isScopedToLocation(
  locationId: string,
  entries: Partial<Record<BbTable, BbEntry[]>>,
): boolean {
  return (Object.values(entries) as Array<BbEntry[] | undefined>).some((list) =>
    (list ?? []).some(
      (row) =>
        row.deleted_at === null &&
        row.id !== locationId &&
        row.location_scope === locationId,
    ),
  );
}

/** L9 #10 feedback: a streetless draft says where its city will go. */
function draftRoutingHint(parts: AddressParts): ReactNode | undefined {
  if (parts.street?.trim()) return undefined;
  const city = parts.city?.trim();
  if (!city) return undefined;
  const label = normalizeCoverageLabel(
    [city, parts.state?.trim()].filter(Boolean).join(', '),
  );
  return label
    ? `Street needed to save as a location — closing moves ${label} to Also serving.`
    : 'Street needed to save as a location.';
}

export default function LocationList({
  primaryDetails,
  primaryContact,
  onPrimaryDetailsPatch,
}: {
  primaryDetails?: ReactNode;
  /** Current Primary phone/email — shown as a fallback line on other rows. */
  primaryContact?: { phone: string; email: string };
  /** Primary's four columns still buffer until Save A (same as the old strip). */
  onPrimaryDetailsPatch?: (patch: LocationDetailsPatch) => void;
} = {}) {
  const locations = useBrandBibleWizardStore(selectEntries('locations'));
  const entries = useBrandBibleWizardStore((s) => s.entries);
  const upsertEntry = useBrandBibleWizardStore((s) => s.upsertEntry);
  const discardEntry = useBrandBibleWizardStore((s) => s.discardEntry);
  // The tenant this component rendered for: every write names it, and the store refuses one for a tenant it has left.
  const bbTenant = useBrandBibleWizardStore((s) => s.tenantId) ?? '';
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftParts, setDraftParts] = useState<AddressParts | null>(null);
  const draftPostingRef = useRef(false);
  // L9 #11: the row + fields whose incomplete blur was refused — the silent
  // no-op becomes a visible incomplete-state until a complete commit lands.
  const [incomplete, setIncomplete] = useState<{
    rowId: string;
    fields: LocationPartField[];
  } | null>(null);
  // L9 #14: 'Saving…' feedback while the draft POST is in flight (Doherty
  // p119/p123 — processing must speak), and the optimistic ★ swap id.
  const [savingDraft, setSavingDraft] = useState(false);
  const [optimisticPrimaryId, setOptimisticPrimaryId] = useState<string | null>(null);

  const sites = locations.filter(isAddressLocation);
  // The star follows the optimistic swap while it is in flight (L9 #14).
  const storePrimaryId =
    sites.find((site) => site.location_key === 'primary')?.id ?? null;
  const effectivePrimaryId = optimisticPrimaryId ?? storePrimaryId;

  async function updateParts(row: BbEntry<'locations'>, next: AddressParts) {
    const missing = REQUIRED_PARTS.filter((f) => !(next[f]?.trim() ?? ''));
    if (missing.length > 0) {
      setIncomplete({ rowId: row.id, fields: missing });
      return;
    }
    if (incomplete?.rowId === row.id) setIncomplete(null);
    const patch: BbPatchPayload<'locations'> & AddressParts = {
      address: composeAddress(next),
    };
    if (hasTypedLocationParts(row)) {
      patch.street = next.street ?? '';
      patch.suite = next.suite ?? '';
      patch.city = next.city ?? '';
      patch.state = next.state ?? '';
      patch.zip = next.zip ?? '';
    }
    try {
      await upsertEntry('locations', patch, row.id, bbTenant);
    } catch (e: unknown) {
      message.error(`Failed to save location — ${e instanceof Error ? e.message : 'request failed'}`);
    }
  }

  async function updateDetails(row: BbEntry<'locations'>, patch: LocationDetailsPatch) {
    const isPrimaryRow = row.id === effectivePrimaryId;
    if (isPrimaryRow && onPrimaryDetailsPatch) {
      onPrimaryDetailsPatch(patch);
      return;
    }
    try {
      await upsertEntry('locations', patch as BbPatchPayload<'locations'>, row.id, bbTenant);
    } catch (e: unknown) {
      message.error(`Failed to save location details — ${e instanceof Error ? e.message : 'request failed'}`);
    }
  }

  async function persistDraft(parts: AddressParts) {
    // L9 #10 live-bug fix: a list row requires a STREET. nameFromStreet used
    // to fall back to city, so a city-only draft saved an unlabeled
    // address-less row wearing full building-row chrome. City-only input
    // routes to the Also-serving band on close (closeDraft) — never here.
    const street = parts.street?.trim() ?? '';
    if (!street) return;
    if (draftPostingRef.current) return;
    draftPostingRef.current = true;
    setSavingDraft(true);
    const name = nameFromStreet(street, parts.city);
    const slug = slugFromLocationName(name || street) || 'location';
    try {
      await upsertEntry('locations', {
        location_key: slug,
        address: composeAddress(parts),
      }, undefined, bbTenant);
      setDraftParts(null);
    } catch (e: unknown) {
      message.error(`Failed to add location — ${e instanceof Error ? e.message : 'request failed'}`);
    } finally {
      draftPostingRef.current = false;
      setSavingDraft(false);
    }
  }

  /** A streetless draft's city, when it is a valid "City, ST", becomes a
   *  coverage metro row instead of dying with the draft (L9 #10). */
  function routeDraftCityToCoverage(parts: AddressParts): boolean {
    const raw = [parts.city?.trim(), parts.state?.trim()].filter(Boolean).join(', ');
    const label = normalizeCoverageLabel(raw);
    if (!label) return false;
    const existing = locations
      .filter(isMetroCoverageRow)
      .map((row) => (row.metro ?? '').trim());
    if (hasCoverageLabel(existing, label)) return false;
    void upsertEntry('locations', {
      location_key: coverageRowKey(label),
      metro: label,
    }, undefined, bbTenant).catch((e: unknown) => {
      message.error(
        `Failed to add service area — ${e instanceof Error ? e.message : 'request failed'}`,
      );
    });
    return true;
  }

  function openDraft() {
    setDraftParts(draftParts ?? {});
    setEditingId('draft');
  }

  function closeDraft() {
    const street = draftParts?.street?.trim() ?? '';
    if (!street && draftParts) {
      // Streetless drafts never become rows; a valid City, ST routes to the
      // Also-serving band instead of vanishing (L9 #10).
      const routed = routeDraftCityToCoverage(draftParts);
      if (routed || !nameFromStreet(street, draftParts?.city)) setDraftParts(null);
    }
    setEditingId((current) => (current === 'draft' ? null : current));
  }

  async function promoteLocation(row: BbEntry<'locations'>) {
    const primary = sites.find((site) => site.location_key === 'primary');
    if (!primary || primary.id === row.id) return;
    const otherKey =
      row.location_key && row.location_key !== 'primary'
        ? row.location_key
        : slugFromLocationName(displayLocationName(row.location_key, getLocationParts(row))) ||
          'location';
    // L9 #14: optimistic ★ — the swap renders immediately and reverts with an
    // error toast if either PATCH fails (Doherty p126). The details strip
    // follows the effective star, which is the model users hold.
    setOptimisticPrimaryId(row.id);
    try {
      await upsertEntry('locations', { location_key: otherKey }, primary.id, bbTenant);
      await upsertEntry('locations', { location_key: 'primary' }, row.id, bbTenant);
    } catch (e: unknown) {
      message.error(`Failed to set primary — ${e instanceof Error ? e.message : 'request failed'}`);
    } finally {
      setOptimisticPrimaryId(null);
    }
  }

  async function removeLocation(row: BbEntry<'locations'>) {
    // D57: a place a gather found and a person confirmed is remembered once removed, so ask first.
    if (isConfirmedGathered(row)) {
      const name = displayLocationName(row.location_key, getLocationParts(row));
      if (!(await confirmRemoveGathered(name))) return;
    }
    try {
      await discardEntry('locations', row.id, bbTenant);
    } catch (e: unknown) {
      message.error(`Failed to remove location — ${e instanceof Error ? e.message : 'request failed'}`);
    }
  }

  return (
    <div data-testid="bb-location-list" className="mt-2 rounded-[9px] border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        {/* L9 #12: 17px/600 — the top step of the card's 5-step scale
            (11/12/13/14/17), so the card header outranks its 14px/600 rows.
            Lupton #2's literal 'text-sm' assumed 14/medium rows; live rows
            are 14/600, so size — not weight — carries the rank. */}
        <p className="text-[17px] font-semibold text-foreground">
          Locations · {sites.length + (draftParts ? 1 : 0)}
        </p>
      </div>

      <div className="space-y-2.5 p-4">
        {sites.map((row) => {
          const candidate = toLocationCandidate(row);
          const isPrimary = row.id === effectivePrimaryId;
          const scoped = isScopedToLocation(row.id, entries);
          return (
            <MergeLocationRow
              key={row.id}
              location={{
                ...candidate,
                locationKey: displayLocationName(row.location_key, candidate.parts),
              }}
              isPrimary={isPrimary}
              editing={editingId === row.id}
              onToggleEdit={() => {
                if (editingId === row.id) setIncomplete(null);
                setEditingId((current) => (current === row.id ? null : row.id));
              }}
              onPromote={isPrimary ? undefined : () => void promoteLocation(row)}
              onUpdateField={(parts) => void updateParts(row, parts)}
              invalidFields={incomplete?.rowId === row.id ? incomplete.fields : undefined}
              editHint={
                incomplete?.rowId === row.id
                  ? 'Street, city, state and ZIP needed to save.'
                  : undefined
              }
              onRemove={isPrimary ? undefined : () => void removeLocation(row)}
              removeDisabled={scoped}
              removeDisabledReason={
                scoped ? 'Other fields are scoped to this location' : undefined
              }
              primaryDetails={isPrimary ? primaryDetails : undefined}
              rowDetails={
                <LocationRowDetails
                  locationId={row.id}
                  radius={row.service_radius_miles}
                  trackingPhone={row.tracking_phone}
                  gbpUrl={row.gbp_url}
                  hours={row.hours}
                  contactFallback={isPrimary ? undefined : (primaryContact ?? { phone: '', email: '' })}
                  onPatch={(patch) => void updateDetails(row, patch)}
                />
              }
            />
          );
        })}

        {/* No primary row yet: Phone and Email (the primary location's details) must still be enterable. */}
        {!effectivePrimaryId && primaryDetails}

        {draftParts && (
          <MergeLocationRow
            location={{
              id: 'draft',
              locationKey: displayLocationName(undefined, draftParts),
              verified: false,
              parts: draftParts,
            }}
            editing={editingId === 'draft'}
            onToggleEdit={() =>
              editingId === 'draft' ? closeDraft() : setEditingId('draft')
            }
            onUpdateField={(parts) => {
              setDraftParts(parts);
              void persistDraft(parts);
            }}
            editHint={
              savingDraft ? (
                <span data-testid="bb-location-draft-saving">Saving…</span>
              ) : (
                draftRoutingHint(draftParts)
              )
            }
            fieldsDisabled={savingDraft}
            onRemove={() => {
              setDraftParts(null);
              setEditingId(null);
            }}
          />
        )}

        <button
          type="button"
          data-testid="bb-location-add"
          onClick={openDraft}
          className="w-full rounded-[9px] border border-dashed border-border px-3 py-2 text-xs font-medium text-muted-foreground transition-colors hover:border-coral hover:text-coral-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          + Add location
        </button>
      </div>

      {/* L9 #9: coverage-without-building lives in this labeled band, never
          in the address list above. No star/badge/Edit — a served metro is a
          different species, not a broken row. */}
      <AlsoServingBand />
    </div>
  );
}
